import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A text field that saves when focus leaves it or after a second without
 * typing, not on every keystroke. Follows the remote value whenever there is
 * nothing unsaved here: while the field is not being edited, and also while it
 * is focused but untouched, so the other person's edit shows up in a field one
 * has merely tapped into.
 *
 * Only what was typed here is ever saved. Tapping into a field and leaving it
 * writes nothing, so it can never put back an older value over the other
 * person's newer one; and anything still unsaved when the field goes away (the
 * sheet swiped shut mid-word, which removes it without a blur) is saved then.
 */
export function useDraft(remote: string, save: (value: string) => void, delay = 1000) {
  const [value, setValue] = useState(remote)
  const [seen, setSeen] = useState(remote)
  const timer = useRef<number | undefined>(undefined)
  // typed here and not yet saved: state for rendering, a ref for the callbacks
  const [unsaved, setUnsaved] = useState(false)
  const dirty = useRef(false)
  const latest = useRef(remote)
  const saveRef = useRef(save)

  // adjust state when the remote value changes, without an effect
  if (remote !== seen) {
    setSeen(remote)
    if (!unsaved) setValue(remote)
  }

  useEffect(() => {
    saveRef.current = save
  }, [save])

  useEffect(() => {
    if (!dirty.current) latest.current = remote
  }, [remote])

  const commit = useCallback(() => {
    window.clearTimeout(timer.current)
    if (!dirty.current) return
    dirty.current = false
    setUnsaved(false)
    saveRef.current(latest.current)
  }, [])

  const onChange = useCallback(
    (next: string) => {
      latest.current = next
      dirty.current = true
      setUnsaved(true)
      setValue(next)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(commit, delay)
    },
    [commit, delay],
  )

  const onFocus = useCallback(() => {}, [])

  const onBlur = useCallback(() => {
    commit()
  }, [commit])

  // saved on the way out, whatever closed it
  useEffect(() => commit, [commit])

  return { value, onChange, onFocus, onBlur, commit }
}
